import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { LogIn, ArrowLeft, ArrowRight } from 'lucide-react';
import { LoginModal } from './LoginModal';
import { Header } from './Header';

const LOGO_URL = (process.env.PUBLIC_URL || '') + '/logo.png';

const DistortedText = ({ children, className = '' }) => {
  const containerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: -1000, y: -1000 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isReady, setIsReady] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
        if (rect.width > 0 && rect.height > 0) {
          setIsReady(true);
        }
      }
    };

    const initTimer = setTimeout(updateDimensions, 100);

    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    const handleGlobalMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.sqrt(
        Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
      );
      if (distance > 1000) {
        setMousePos({ x: -1000, y: -1000 });
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('resize', updateDimensions);
    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
    }
    updateDimensions();

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('resize', updateDimensions);
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
      }
      clearTimeout(initTimer);
    };
  }, []);

  const getWaterDistortion = (charX, charY, charWidth) => {
    const dx = mousePos.x - (charX + charWidth / 2);
    const dy = mousePos.y - charY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const radius = 150;

    if (distance > radius) {
      return { waveX: 0, waveY: 0, rgbShift: 0, scaleX: 1, scaleY: 1, skewX: 0, skewY: 0 };
    }

    const falloff = 1 - (distance / radius);
    const smoothFalloff = Math.pow(falloff, 2);

    const waveStrength = smoothFalloff * 12;
    const wave = Math.sin((distance / radius) * Math.PI * 3) * waveStrength;

    const angle = Math.atan2(dy, dx);
    const waveX = Math.cos(angle) * wave;
    const waveY = Math.sin(angle) * wave;

    const bulge = smoothFalloff * 0.25;
    const scaleX = 1 + bulge;
    const scaleY = 1 - bulge * 0.5;

    const shearStrength = smoothFalloff * 8;
    const skewX = (dy / (distance || 1)) * shearStrength;
    const skewY = -(dx / (distance || 1)) * shearStrength;

    return {
      waveX,
      waveY,
      rgbShift: smoothFalloff * 5,
      scaleX,
      scaleY,
      skewX,
      skewY
    };
  };

  const text = String(children);
  const chars = text.split('');

  const isGradient = className.includes('gradient-text');

  return (
    <span
      ref={containerRef}
      className="relative inline-block"
      style={{
        perspective: '1000px',
        position: 'relative'
      }}
    >
      {!isReady ? (
        <span style={{
          background: isGradient ? 'linear-gradient(135deg, #D4AF37 0%, #F5DEB3 50%, #D4AF37 100%)' : 'inherit',
          WebkitBackgroundClip: isGradient ? 'text' : 'unset',
          WebkitTextFillColor: isGradient ? 'transparent' : 'inherit',
          backgroundClip: isGradient ? 'text' : 'unset',
          fontSize: 'inherit',
          fontWeight: 'inherit'
        }}>{children}</span>
      ) : (
      <span
        ref={textRef}
        className="relative"
        style={{ position: 'relative', display: 'inline-block' }}
      >
        {chars.map((char, i) => {
          const charWidth = dimensions.width / (chars.length || 1);
          const charX = i * charWidth;
          const charY = dimensions.height / 2;
          const dist = getWaterDistortion(charX, charY, charWidth);

          const isAffected = dist.rgbShift > 0;

          return (
            <span
              key={i}
              className="inline-block relative"
              style={{
                display: 'inline-block',
                transform: `
                  translate(${dist.waveX}px, ${dist.waveY}px)
                  scaleX(${dist.scaleX})
                  scaleY(${dist.scaleY})
                  skewX(${dist.skewX}deg)
                  skewY(${dist.skewY}deg)
                `,
                transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
                transformOrigin: 'center center',
                position: 'relative',
                zIndex: 1
              }}
            >
              {isAffected && (
                <>
                  <span
                    className="absolute inset-0"
                    style={{
                      transform: `
                        translate(${-dist.rgbShift - 1}px, ${-dist.rgbShift * 0.3}px)
                        scaleX(${dist.scaleX * 1.01})
                        scaleY(${dist.scaleY})
                        skewX(${dist.skewX}deg)
                        skewY(${dist.skewY}deg)
                      `,
                      color: '#ff0000',
                      opacity: 0.7,
                      mixBlendMode: 'screen',
                      filter: 'blur(0.3px)'
                    }}
                    aria-hidden="true"
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                  <span
                    className="absolute inset-0"
                    style={{
                      transform: `
                        translate(${dist.rgbShift + 1}px, ${dist.rgbShift * 0.3}px)
                        scaleX(${dist.scaleX * 1.01})
                        scaleY(${dist.scaleY})
                        skewX(${dist.skewX}deg)
                        skewY(${dist.skewY}deg)
                      `,
                      color: '#00ffff',
                      opacity: 0.7,
                      mixBlendMode: 'screen',
                      filter: 'blur(0.3px)'
                    }}
                    aria-hidden="true"
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                  <span
                    className="absolute inset-0"
                    style={{
                      transform: `
                        translate(0px, ${dist.rgbShift + 1}px)
                        scaleX(${dist.scaleX})
                        scaleY(${dist.scaleY * 1.01})
                        skewX(${dist.skewX}deg)
                        skewY(${dist.skewY}deg)
                      `,
                      color: '#00ff00',
                      opacity: 0.5,
                      mixBlendMode: 'screen',
                      filter: 'blur(0.5px)'
                    }}
                    aria-hidden="true"
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                </>
              )}
              <span style={{ position: 'relative', zIndex: 3 }}>
                {char === ' ' ? '\u00A0' : char}
              </span>
            </span>
          );
        })}
      </span>
      )}
    </span>
  );
};

const IMAGES = {
  roadmap: '/images/features/personal-1-ip.jpg',
  cinematic: '/images/features/personal-3-video.jpg',
  neon: '/images/features/business-1-brand.jpg',
  studio: '/images/features/personal-2-copy.jpg',
  city: '/images/features/personal-4-data.jpg',
  music: '/images/features/personal-5-rewrite.jpg',
  abstract: '/images/features/business-5-analysis.jpg',
  card1: '/images/features/personal-6-account.jpg',
  card2: '/images/features/business-2-copy.jpg',
  card3: '/images/features/business-3-video.jpg',
  card4: '/images/features/business-4-analytics.jpg',
  card5: '/images/features/business-6-crm.jpg',
  bg1: '/images/backgrounds/bg1.jpg',
  bg2: '/images/backgrounds/bg2.jpg',
  bg3: '/images/backgrounds/bg3.jpg',
  bg4: '/images/backgrounds/bg4.jpg',
  bg5: '/images/backgrounds/bg5.jpg',
  bg6: '/images/backgrounds/bg6.jpg',
  bg7: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1200&q=80&auto=format'
};

const preloadedImages = {};

const preloadImage = (src) => {
  return new Promise((resolve) => {
    if (preloadedImages[src]) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => {
      preloadedImages[src] = true;
      resolve();
    };
    img.onerror = resolve;
    img.src = src;
  });
};

const PageBackground = ({ children, enableMouseInteraction = true }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [imagesReady, setImagesReady] = useState(true);
  const [time, setTime] = useState(0);
  const [floatingIndex] = useState(Math.floor(Math.random() * 7));
  
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const scrollYRef = useRef(0);

  useEffect(() => {
    mousePositionRef.current = mousePosition;
  }, [mousePosition]);

  useEffect(() => {
    scrollYRef.current = scrollY;
  }, [scrollY]);

  useEffect(() => {
    const loadAllImages = async () => {
      try {
        await Promise.all(Object.values(IMAGES).map(preloadImage));
      } catch (e) {
      }
      setImagesReady(true);
    };
    loadAllImages();

    let animationFrame;
    const containerRef = document.getElementById('bg-float-container');
    const animate = () => {
      if (containerRef) {
        const t = performance.now() / 1000;
        const children = containerRef.children;
        for (let i = 0; i < children.length; i++) {
          if (i === floatingIndex) {
            const child = children[i];
            const img = backgroundImages[i];
            const currentMousePos = mousePositionRef.current;
            const currentScrollY = scrollYRef.current;
            
            const parallaxX = (currentMousePos.x - window.innerWidth / 2) * img.speed * 0.005;
            const parallaxY = (currentMousePos.y - window.innerHeight / 2) * img.speed * 0.005;
            const scrollParallax = currentScrollY * img.speed * 0.15;
            
            const floatX = parallaxX + Math.sin(t * 0.2) * 12;
            const floatY = parallaxY + scrollParallax + Math.cos(t * 0.18) * 10;
            const floatRotate = img.rotate + Math.sin(t * 0.15) * 3;
            
            child.style.transform = `rotate(${floatRotate}deg) translate(${floatX}px, ${floatY}px)`;
          }
        }
      }
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    if (enableMouseInteraction) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      cancelAnimationFrame(animationFrame);
      if (enableMouseInteraction) {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [enableMouseInteraction, floatingIndex]);

  const backgroundImages = [
    { src: IMAGES.bg1, top: '10%', left: '-8%', width: '520px', height: '360px', rotate: -18, zIndex: 1, speed: 0.12, opacity: 0.35 },
    { src: IMAGES.bg2, top: '8%', right: '-12%', width: '450px', height: '600px', rotate: 12, zIndex: 3, speed: 0.08, opacity: 0.3 },
    { src: IMAGES.bg3, top: '55%', left: '-10%', width: '500px', height: '340px', rotate: 8, zIndex: 2, speed: 0.14, opacity: 0.4 },
    { src: IMAGES.bg4, top: '30%', right: '-10%', width: '580px', height: '420px', rotate: -10, zIndex: 1, speed: 0.10, opacity: 0.32 },
    { src: IMAGES.bg5, top: '75%', left: '5%', width: '420px', height: '520px', rotate: 20, zIndex: 3, speed: 0.11, opacity: 0.28 },
    { src: IMAGES.bg6, top: '85%', right: '-15%', width: '550px', height: '380px', rotate: -15, zIndex: 2, speed: 0.09, opacity: 0.38 },
    { src: IMAGES.bg7, top: '45%', left: '80%', width: '480px', height: '450px', rotate: 15, zIndex: 1, speed: 0.13, opacity: 0.25 },
  ];

  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      <div
        id="bg-float-container"
        className="fixed inset-0 pointer-events-none"
        style={{
          opacity: imagesReady ? 1 : 0,
          transition: 'opacity 0.5s ease-out'
        }}
      >
        {backgroundImages.map((img, i) => {
          if (enableMouseInteraction) {
            const parallaxX = (mousePosition.x - window.innerWidth / 2) * img.speed * 0.005;
            const parallaxY = (mousePosition.y - window.innerHeight / 2) * img.speed * 0.005;
            const scrollParallax = scrollY * img.speed * 0.15;

            const imageCenterX = (() => {
              if (img.left) {
                const left = parseFloat(img.left);
                return window.innerWidth * (left < 0 ? 0.15 : 0.5);
              } else if (img.right) {
                return window.innerWidth * 0.85;
              }
              return window.innerWidth / 2;
            })();

            const imageCenterY = (() => {
              const top = parseFloat(img.top);
              return window.innerHeight * (top / 100);
            })();

            const dx = mousePosition.x - imageCenterX;
            const dy = mousePosition.y - imageCenterY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const hoverFalloff = Math.max(0, 1 - distance / 400);
            const hoverScale = 1 + hoverFalloff * 0.03;
            const hoverRotate = img.rotate + hoverFalloff * 1;
            const hoverBrightness = 1 + hoverFalloff * 0.1;
            const hoverBlur = 0.5 * (1 - hoverFalloff);

            return (
              <div
                key={i}
                className="absolute rounded-3xl overflow-hidden cursor-crosshair"
                style={{
                  top: img.top,
                  left: img.left,
                  right: img.right,
                  width: img.width,
                  height: img.height,
                  transform: `rotate(${img.rotate}deg) translate(${parallaxX}px, ${parallaxY + scrollParallax}px)`,
                  zIndex: img.zIndex + (hoverFalloff > 0 ? 5 : 0),
                  opacity: img.opacity,
                  transition: 'transform 0.4s ease-out, filter 0.5s ease, z-index 0.1s',
                  filter: `brightness(${hoverBrightness})`,
                  boxShadow: hoverFalloff > 0
                    ? '0 0 60px rgba(212,175,55,0.4), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'auto'
                }}
              >
                <img
                  src={img.src}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(135deg, rgba(10,10,10,${0.1 + (1 - hoverFalloff) * 0.2}) 0%, rgba(10,10,10,${0.4 + (1 - hoverFalloff) * 0.3}) 100%)`
                  }}
                />
                {hoverFalloff > 0 && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at ${
                        ((mousePosition.x - (imageCenterX + parallaxX)) / parseFloat(img.width) + 0.5) * 100
                      }% ${
                        ((mousePosition.y - (imageCenterY + parallaxY)) / parseFloat(img.height) + 0.5) * 100
                      }%, rgba(212,175,55,${hoverFalloff * 0.35}) 0%, transparent 50%)`
                    }}
                  />
                )}
              </div>
            );
          } else {
            const floatX = Math.sin(time * (0.3 + i * 0.1)) * (8 + i * 2);
            const floatY = Math.cos(time * (0.25 + i * 0.08)) * (6 + i * 1.5);
            const floatRotate = img.rotate + Math.sin(time * (0.2 + i * 0.05)) * (2 + i * 0.3);
            const floatScale = 1 + Math.sin(time * (0.35 + i * 0.07)) * 0.03;
            const perspectiveZ = Math.sin(time * (0.15 + i * 0.04)) * 20;

            return (
              <div
                key={i}
                className="absolute rounded-3xl overflow-hidden"
                data-base-rotate={img.rotate}
                style={{
                  top: img.top,
                  left: img.left,
                  right: img.right,
                  width: img.width,
                  height: img.height,
                  transform: `
                    rotate(${floatRotate}deg)
                    translate(${floatX}px, ${floatY}px)
                    scale(${floatScale})
                    translateZ(${perspectiveZ}px)
                  `,
                  transformStyle: 'preserve-3d',
                  zIndex: img.zIndex,
                  opacity: img.opacity,
                  transition: 'none'
                }}
              >
                <img
                  src={img.src}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(10,10,10,0.2) 0%, rgba(10,10,10,0.55) 100%)'
                  }}
                />
              </div>
            );
          }
        })}
      </div>

      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-luxury-gold/8 to-transparent blur-[120px]" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-amber-300/6 to-transparent blur-[100px]" />
        <div className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-luxury-gold/5 to-transparent blur-[80px]" />
      </div>

      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />

      <div
        className="fixed w-[400px] h-[400px] rounded-full pointer-events-none z-50 mix-blend-overlay"
        style={{
          left: mousePosition.x - 200,
          top: mousePosition.y - 200,
          background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
          transition: 'opacity 0.3s ease-out'
        }}
      />

      <div className="relative z-10">
        {children}
      </div>

      <style>{`
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(40px, -40px) scale(1.1); }
          50% { transform: translate(0, -80px) scale(1); }
          75% { transform: translate(-40px, -40px) scale(0.9); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-30px, 30px) scale(1.05); }
          50% { transform: translate(0, 60px) scale(1); }
          75% { transform: translate(30px, 30px) scale(0.95); }
        }
        @keyframes float-fast {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(0, -50px) scale(1.15); }
        }
        .animate-float-slow { animation: float-slow 12s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 8s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 8s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 6s ease-in-out infinite; }
        @keyframes bounceDot {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
          40% { transform: translateY(-12px); opacity: 1; }
        }
        @keyframes shimmerProgress {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes luxurySpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        #logo-video {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
          contain: layout style paint;
        }
      `}</style>
    </div>
  );
};

const ProtectedRoute = ({ children, isLoggedIn, onLoginClick }) => {
  if (!isLoggedIn) {
    return (
      <PageBackground>
        <Header onLoginClick={onLoginClick} isLoggedIn={false} />
        <main className="relative min-h-screen flex items-center justify-center">
          <div className="max-w-md mx-auto text-center px-6">
            <div className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-luxury-gold/30 to-amber-300/30 flex items-center justify-center">
              <LogIn className="w-12 h-12 text-luxury-gold" />
            </div>
            <h1 className="text-3xl font-bold gradient-text mb-4">请先登录</h1>
            <p className="text-apple-gray mb-8">您需要登录后才能访问此功能</p>
            <div className="space-y-4">
              <button
                onClick={onLoginClick}
                className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-luxury-gold via-amber-300 to-luxury-gold bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-700 shadow-lg shadow-luxury-gold/20 hover:shadow-luxury-gold/40 flex items-center justify-center gap-3"
              >
                <LogIn className="w-5 h-5" />
                立即登录
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full py-4 rounded-xl font-semibold text-apple-gray border border-white/15 hover:border-white/25 hover:bg-white/8 transition-all duration-400 flex items-center justify-center gap-3"
              >
                <ArrowLeft className="w-5 h-5" />
                取消
              </button>
            </div>
          </div>
        </main>
      </PageBackground>
    );
  }
  return children;
};

const BackButton = () => (
  <Link
    to="/"
    className="inline-flex items-center gap-2 px-6 py-3 glass-card border border-white/15 rounded-xl mb-8 hover:border-luxury-gold/50 transition-all group"
  >
    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
    <span>返回首页</span>
  </Link>
);

const PageTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-4 mb-8">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-luxury-gold/30 to-amber-300/30 flex items-center justify-center">
      <Icon className="w-7 h-7 text-luxury-gold" />
    </div>
    <div>
      <h1 className="text-4xl font-bold gradient-text">{title}</h1>
      <p className="text-apple-gray">{subtitle}</p>
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc, image, to, index, colorMode = 'personal' }) => {
  const isPersonal = colorMode === 'personal';
  const mainColor = isPersonal ? 'orange-400' : 'cyan-400';
  const subColor = isPersonal ? 'amber-400' : 'blue-400';
  const mainClass = `text-${mainColor}`;
  const bgClass = `from-${mainColor}/15 to-${subColor}/10`;
  const cardRef = useRef(null);

  const handleMouseEnter = () => {
    gsap.to(cardRef.current, {
      y: -15,
      scale: 1.03,
      duration: 0.5,
      ease: 'power3.out'
    });
  };

  const handleMouseLeave = () => {
    gsap.to(cardRef.current, {
      y: 0,
      scale: 1,
      duration: 0.5,
      ease: 'power3.out'
    });
  };

  return (
    <Link
      to={to}
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="group relative cursor-pointer perspective-1000"
    >
      <div className={`relative glass-card border border-white/15 rounded-3xl overflow-hidden hover:border-${mainColor}/25 transition-all duration-700 shadow-xl group-hover:shadow-${mainColor}/10`}>
        <div className="relative h-64 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-125"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-dark-bg/50 to-transparent transition-transform duration-1000 group-hover:scale-125" />
          <div className="absolute top-5 left-5 flex gap-2">
            <div className={`w-12 h-12 rounded-2xl glass-card border border-white/20 flex items-center justify-center backdrop-blur-2xl shadow-lg bg-gradient-to-br ${bgClass}`}>
              <Icon className={`w-6 h-6 ${mainClass}`} />
            </div>
          </div>
          <div className="absolute top-5 right-5">
              <div className={`w-3 h-3 rounded-full ${mainColor}/60 animate-ping`} />
            </div>
          <div className="absolute bottom-5 left-5 right-5">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-xs font-mono ${mainClass} tracking-[0.2em]`}>0{index + 1}</span>
              <div className={`flex-1 h-px bg-gradient-to-r from-${mainColor}/40 to-transparent`} />
            </div>
            <h3 className="text-2xl font-bold">{title}</h3>
          </div>
        </div>
        <div className="p-7">
          <p className="text-apple-gray mb-5 leading-relaxed">{desc}</p>
          <div className={`flex items-center gap-3 ${mainClass}/80 font-semibold group-hover:gap-5 transition-all duration-500`}>
            <span>进入系统</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-3 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export {
  DistortedText,
  IMAGES,
  LOGO_URL,
  PageBackground,
  Header,
  BackButton,
  PageTitle,
  FeatureCard,
  LoginModal,
  ProtectedRoute
};
